// src/pages/finance-portal/cash-position/CashPositionDashboard.js
//
// Real-Time Cash Position Dashboard
//
// Aggregates cash across all linked accounts. Visualizes totals, breakdown
// by institution / type / currency, balance trend, and per-account freshness.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    LinearProgress,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import CategoryIcon from '@mui/icons-material/Category';
import LanguageIcon from '@mui/icons-material/Language';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#3b82f6';
const ACCENT_DARK = '#1e40af';

const TYPE_COLORS = {
    CHECKING: '#22c55e',
    SAVINGS: '#3b82f6',
    CREDIT: '#f59e0b',
    LOAN: '#ef4444',
    INVESTMENT: '#8b5cf6',
    OTHER: '#64748b',
};

function formatCurrency(amount, currency = 'USD') {
    if (amount == null || Number.isNaN(amount)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
    }).format(amount);
}

function formatTimeAgo(iso) {
    if (!iso) return 'never';
    const then = new Date(iso).getTime();
    const diff = Date.now() - then;
    if (diff < 60_000) return 'just now';
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} hr ago`;
    return `${Math.floor(diff / 86_400_000)} day(s) ago`;
}

function StatCard({ icon, label, value, subtitle, color = ACCENT, onClick }) {
    return (
        <Card
            onClick={onClick}
            sx={{
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'all 0.2s ease',
                '&:hover': onClick
                    ? { borderColor: color, transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }
                    : {},
            }}
        >
            <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: `${color}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>
                        {icon}
                    </Box>
                    {onClick && <ArrowForwardIcon sx={{ fontSize: 16, color: '#cbd5e1' }} />}
                </Box>
                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {label}
                </Typography>
                <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.6rem', color: '#0f172a', mt: 0.5, lineHeight: 1.2 }}>
                    {value}
                </Typography>
                {subtitle && (
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', mt: 0.5 }}>
                        {subtitle}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
}

function TrendSparkline({ series }) {
    if (!series || series.length === 0) return null;
    const values = series.map(s => s.total);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const w = 100;
    const h = 28;
    const step = w / Math.max(series.length - 1, 1);
    const path = series
        .map((p, i) => {
            const x = i * step;
            const y = h - ((p.total - min) / range) * h;
            return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
        })
        .join(' ');
    return (
        <svg viewBox={`0 0 ${w} ${h}`} width="100%" height="40" preserveAspectRatio="none">
            <path d={path} fill="none" stroke={ACCENT} strokeWidth="1.5" />
        </svg>
    );
}

function TrendChart({ series }) {
    if (!series || series.length === 0) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography sx={{ fontFamily, color: '#94a3b8' }}>
                    No trend data yet — take a snapshot to start.
                </Typography>
            </Box>
        );
    }
    const values = series.map(s => s.total);
    const min = Math.min(...values, 0);
    const max = Math.max(...values, 0);
    const range = max - min || 1;
    const w = 800;
    const h = 200;
    const padX = 50;
    const padY = 20;
    const usableW = w - padX * 2;
    const usableH = h - padY * 2;
    const step = usableW / Math.max(series.length - 1, 1);

    const path = series
        .map((p, i) => {
            const x = padX + i * step;
            const y = padY + usableH - ((p.total - min) / range) * usableH;
            return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
        })
        .join(' ');
    const fillPath = `${path} L ${padX + (series.length - 1) * step} ${padY + usableH} L ${padX} ${padY + usableH} Z`;

    return (
        <svg viewBox={`0 0 ${w} ${h}`} width="100%" height="220">
            <defs>
                <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={ACCENT} stopOpacity="0.25" />
                    <stop offset="100%" stopColor={ACCENT} stopOpacity="0" />
                </linearGradient>
            </defs>
            {[0, 0.25, 0.5, 0.75, 1].map((p) => {
                const y = padY + usableH * p;
                const v = max - range * p;
                return (
                    <g key={p}>
                        <line x1={padX} y1={y} x2={w - padX} y2={y} stroke="#e2e8f0" strokeDasharray="2 4" />
                        <text x={padX - 6} y={y + 3} textAnchor="end" fontSize="9" fill="#94a3b8" fontFamily={fontFamily}>
                            {formatCurrency(v)}
                        </text>
                    </g>
                );
            })}
            <path d={fillPath} fill="url(#trendGrad)" />
            <path d={path} fill="none" stroke={ACCENT} strokeWidth="2" />
            {series.map((p, i) => {
                if (i % Math.ceil(series.length / 8) !== 0 && i !== series.length - 1) return null;
                const x = padX + i * step;
                return (
                    <text key={i} x={x} y={h - 4} textAnchor="middle" fontSize="9" fill="#94a3b8" fontFamily={fontFamily}>
                        {p.date.slice(5)}
                    </text>
                );
            })}
        </svg>
    );
}

function BreakdownTable({ rows, valueKey = 'total', nameKey = 'name', icon }) {
    if (!rows || rows.length === 0) {
        return (
            <Typography sx={{ fontFamily, color: '#94a3b8', textAlign: 'center', py: 3 }}>
                No data
            </Typography>
        );
    }
    const total = rows.reduce((acc, r) => acc + (r[valueKey] || 0), 0) || 1;
    return (
        <Stack spacing={1.5}>
            {rows.map((row, i) => {
                const pct = ((row[valueKey] / total) * 100).toFixed(1);
                return (
                    <Box key={i}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {icon}
                                <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b', fontWeight: 600 }}>
                                    {row[nameKey]}
                                </Typography>
                                <Chip
                                    label={`${row.account_count} acct${row.account_count === 1 ? '' : 's'}`}
                                    size="small"
                                    sx={{ height: 18, fontSize: '0.65rem', fontFamily, bgcolor: '#f1f5f9' }}
                                />
                            </Box>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#0f172a', fontWeight: 700 }}>
                                {formatCurrency(row[valueKey])}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                                variant="determinate"
                                value={Math.min(100, Number(pct))}
                                sx={{
                                    flex: 1,
                                    height: 6,
                                    borderRadius: 3,
                                    bgcolor: '#f1f5f9',
                                    '& .MuiLinearProgress-bar': { bgcolor: ACCENT, borderRadius: 3 },
                                }}
                            />
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', minWidth: 36, textAlign: 'right' }}>
                                {pct}%
                            </Typography>
                        </Box>
                    </Box>
                );
            })}
        </Stack>
    );
}

export default function CashPositionDashboard() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [trendDays, setTrendDays] = useState(30);
    const [isLoading, setIsLoading] = useState(true);
    const [isSnapshotting, setIsSnapshotting] = useState(false);
    const [error, setError] = useState(null);

    const fetchDashboard = useCallback(async (takeSnapshot = false) => {
        setError(null);
        if (!takeSnapshot) setIsLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/cash-position/dashboard', {
                params: { trend_days: trendDays, take_snapshot: takeSnapshot },
            });
            if (res.data?.success) {
                setData(res.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load cash position dashboard.');
        } finally {
            setIsLoading(false);
            setIsSnapshotting(false);
        }
    }, [trendDays]);

    useEffect(() => {
        fetchDashboard(false);
    }, [fetchDashboard]);

    const handleSnapshot = async () => {
        setIsSnapshotting(true);
        try {
            await axiosInstance.post('/api/finance/cash-position/snapshot');
            await fetchDashboard(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Snapshot failed.');
            setIsSnapshotting(false);
        }
    };

    const totals = data?.totals || { current: 0, available: 0, currency: 'USD' };
    const accounts = data?.accounts || [];
    const stale = data?.stale_accounts || 0;
    const trend = data?.trend || [];

    const trendChange = useMemo(() => {
        if (!trend || trend.length < 2) return null;
        const start = trend[0].total;
        const end = trend[trend.length - 1].total;
        const diff = end - start;
        const pct = start === 0 ? 0 : (diff / Math.abs(start)) * 100;
        return { diff, pct };
    }, [trend]);

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, fontFamily, minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.6rem', color: '#0f172a' }}>
                        Cash Position
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mt: 0.5 }}>
                        Real-time view across every linked bank, treasury, and credit account.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1.5}>
                    <Button
                        variant="outlined"
                        startIcon={<CalendarMonthIcon />}
                        onClick={() => navigate('/finance-portal/cash-flow-calendar')}
                        sx={{ fontFamily, textTransform: 'none', borderRadius: '10px' }}
                    >
                        Cash Flow Calendar
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<NotificationsActiveIcon />}
                        onClick={() => navigate('/finance-portal/cash-alerts')}
                        sx={{ fontFamily, textTransform: 'none', borderRadius: '10px' }}
                    >
                        Alerts
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={isSnapshotting ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <SaveIcon />}
                        onClick={handleSnapshot}
                        disabled={isSnapshotting}
                        sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK } }}
                    >
                        Snapshot Now
                    </Button>
                    <IconButton onClick={() => fetchDashboard(false)} title="Refresh">
                        <RefreshIcon />
                    </IconButton>
                </Stack>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2, fontFamily }}>{error}</Alert>
            )}

            {stale > 0 && (
                <Alert
                    severity="warning"
                    icon={<WarningAmberIcon />}
                    sx={{ mb: 2, fontFamily }}
                >
                    {stale} account{stale > 1 ? 's have' : ' has'} not synced in over 24 hours. Reconnect them from <b>Connected Accounts</b>.
                </Alert>
            )}

            {/* KPI cards */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    {isLoading ? (
                        <Skeleton variant="rounded" height={140} />
                    ) : (
                        <StatCard
                            icon={<AccountBalanceIcon />}
                            label="Total Cash"
                            value={formatCurrency(totals.current, totals.currency)}
                            subtitle={`${data?.account_count || 0} accounts`}
                            color={ACCENT}
                        />
                    )}
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    {isLoading ? (
                        <Skeleton variant="rounded" height={140} />
                    ) : (
                        <StatCard
                            icon={<TrendingUpIcon />}
                            label="Available Now"
                            value={formatCurrency(totals.available, totals.currency)}
                            subtitle={`${formatCurrency(totals.current - totals.available, totals.currency)} on hold`}
                            color="#10b981"
                        />
                    )}
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    {isLoading ? (
                        <Skeleton variant="rounded" height={140} />
                    ) : (
                        <StatCard
                            icon={trendChange && trendChange.diff >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                            label={`${trendDays}d Change`}
                            value={trendChange ? formatCurrency(trendChange.diff) : '—'}
                            subtitle={trendChange ? `${trendChange.pct >= 0 ? '+' : ''}${trendChange.pct.toFixed(2)}%` : 'no history'}
                            color={trendChange && trendChange.diff >= 0 ? '#10b981' : '#ef4444'}
                        />
                    )}
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    {isLoading ? (
                        <Skeleton variant="rounded" height={140} />
                    ) : (
                        <StatCard
                            icon={<LocationCityIcon />}
                            label="Institutions"
                            value={(data?.by_institution || []).length}
                            subtitle="diversification"
                            color="#8b5cf6"
                            onClick={() => navigate('/finance-portal/cash-concentration')}
                        />
                    )}
                </Grid>
            </Grid>

            {/* Trend + filters */}
            <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>
                            Cash Balance Trend
                        </Typography>
                        <ToggleButtonGroup
                            value={trendDays}
                            exclusive
                            size="small"
                            onChange={(_, v) => v && setTrendDays(v)}
                            sx={{ '& .MuiToggleButton-root': { fontFamily, textTransform: 'none', px: 2 } }}
                        >
                            <ToggleButton value={7}>7D</ToggleButton>
                            <ToggleButton value={30}>30D</ToggleButton>
                            <ToggleButton value={90}>90D</ToggleButton>
                            <ToggleButton value={365}>1Y</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                    {isLoading ? <Skeleton variant="rounded" height={220} /> : <TrendChart series={trend} />}
                </CardContent>
            </Card>

            {/* Breakdowns */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', color: '#0f172a', mb: 2 }}>
                                By Institution
                            </Typography>
                            <BreakdownTable
                                rows={(data?.by_institution || []).map(r => ({ ...r, name: r.institution_name }))}
                                icon={<LocationCityIcon sx={{ fontSize: 16, color: '#94a3b8' }} />}
                            />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', color: '#0f172a', mb: 2 }}>
                                By Account Type
                            </Typography>
                            <BreakdownTable
                                rows={(data?.by_type || []).map(r => ({ ...r, name: r.type }))}
                                icon={<CategoryIcon sx={{ fontSize: 16, color: '#94a3b8' }} />}
                            />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', color: '#0f172a', mb: 2 }}>
                                By Currency
                            </Typography>
                            <BreakdownTable
                                rows={(data?.by_currency || []).map(r => ({ ...r, name: r.currency }))}
                                icon={<LanguageIcon sx={{ fontSize: 16, color: '#94a3b8' }} />}
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Account detail table */}
            <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <CardContent sx={{ p: 0 }}>
                    <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>
                            Accounts ({accounts.length})
                        </Typography>
                        <Button
                            variant="text"
                            endIcon={<ArrowForwardIcon />}
                            onClick={() => navigate('/finance-portal/connected-accounts')}
                            sx={{ fontFamily, textTransform: 'none' }}
                        >
                            Manage
                        </Button>
                    </Box>
                    {isLoading ? (
                        <Box sx={{ p: 3 }}><Skeleton variant="rounded" height={200} /></Box>
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow sx={{ '& th': { fontFamily, fontWeight: 700, color: '#475569', fontSize: '0.8rem', bgcolor: '#f8fafc' } }}>
                                    <TableCell>Account</TableCell>
                                    <TableCell>Institution</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell align="right">Current</TableCell>
                                    <TableCell align="right">Available</TableCell>
                                    <TableCell>Last Sync</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {accounts.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} sx={{ textAlign: 'center', py: 5, fontFamily, color: '#94a3b8' }}>
                                            No accounts connected yet. Link a bank from <b>Connected Accounts</b>.
                                        </TableCell>
                                    </TableRow>
                                )}
                                {accounts.map((acct) => (
                                    <TableRow key={acct.id} hover>
                                        <TableCell sx={{ fontFamily }}>
                                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#0f172a' }}>
                                                {acct.name || acct.official_name || 'Unnamed'}
                                            </Typography>
                                            {acct.mask && (
                                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>
                                                    ••••{acct.mask}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                            {acct.institution_name}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={acct.type || 'OTHER'}
                                                size="small"
                                                sx={{
                                                    fontFamily,
                                                    fontSize: '0.7rem',
                                                    bgcolor: `${TYPE_COLORS[acct.type] || '#64748b'}14`,
                                                    color: TYPE_COLORS[acct.type] || '#64748b',
                                                    fontWeight: 700,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontFamily, fontWeight: 700, color: '#0f172a' }}>
                                            {formatCurrency(acct.current_balance, acct.iso_currency_code)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontFamily, color: '#475569' }}>
                                            {formatCurrency(acct.available_balance, acct.iso_currency_code)}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={acct.last_synced_at || 'never'}>
                                                <Chip
                                                    label={formatTimeAgo(acct.last_synced_at)}
                                                    size="small"
                                                    sx={{
                                                        fontFamily,
                                                        fontSize: '0.7rem',
                                                        bgcolor: acct.is_stale ? '#fef3c7' : '#dcfce7',
                                                        color: acct.is_stale ? '#92400e' : '#166534',
                                                    }}
                                                />
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}
